import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { LayoutGrid, Pencil, Plus, Search, Sparkles, Tags, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Skeleton,
  StatusBadge,
  Table,
  Td,
  Th,
  Tr,
  useUndo,
} from '@/design/components';
import { cn } from '@/lib/cn';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import { uploadImage } from './upload';
import {
  emptyProduct,
  PRODUCT_PAGE_SIZE,
  useCategoryMutations,
  useProductCategories,
  useProductForEdit,
  useProductMutations,
  useProductSearch,
  type ProductDraft,
} from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * מוצרים ומחלקות (PRD 5.7.1).
 *
 * החיפוש והעימוד רצים בשרת. אחרי חיבור קומקס יש כאן אלפי שורות,
 * וטבלה שמורידה את כולן כדי לסנן בדפדפן נשברת הרבה לפני כן.
 */
export function ProductsAdminPage() {
  const undo = useUndo();
  const canManage = usePerm('products.manage');
  const categories = useProductCategories();
  const { save, remove } = useProductMutations();

  const [term, setTerm] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const found = useProductSearch({ term, categoryId, includeInactive, page });

  if (!canManage) return <Navigate to="/admin" replace />;

  const total = found.data?.total ?? 0;
  const rows = (found.data?.rows ?? []).filter((product) => !undo.isPending(product.id));
  const shownSoFar = page * PRODUCT_PAGE_SIZE + rows.length;

  /** כל שינוי בסינון מחזיר לעמוד הראשון, אחרת מסתכלים על עמוד 4 של חיפוש חדש. */
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  return (
    <AdminPage
      title="מוצרים ומחלקות"
      subtitle="הקטלוג שהעובדים מחפשים בו"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/admin/products/categories" className="no-underline">
            <Button variant="secondary">
              <Tags size={20} strokeWidth={1.75} aria-hidden="true" />
              מחלקות
            </Button>
          </Link>
          <Link to="/admin/products/spotlights" className="no-underline">
            <Button variant="secondary">
              <Sparkles size={20} strokeWidth={1.75} aria-hidden="true" />
              הכר את המוצר
            </Button>
          </Link>
          <Link to="/admin/products/planogram" className="no-underline">
            <Button variant="secondary">
              <LayoutGrid size={20} strokeWidth={1.75} aria-hidden="true" />
              פלנוגרמה
            </Button>
          </Link>
        </div>
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div className="relative">
          <label htmlFor="product-search" className="sr-only">
            חיפוש מוצר
          </label>
          {/* RTL: האייקון ב start, והריפוד נפתח מאותו צד */}
          <Search
            size={20}
            strokeWidth={1.75}
            aria-hidden="true"
            className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            id="product-search"
            type="search"
            value={term}
            onChange={(event) => changeFilter(() => setTerm(event.target.value))}
            placeholder="שם, מק״ט או מחלקה"
            className={`${FIELD} ps-10`}
          />
        </div>

        <div className="grid gap-1">
          <label htmlFor="product-category" className="text-body-sm text-muted">
            מחלקה
          </label>
          <select
            id="product-category"
            value={categoryId ?? ''}
            onChange={(event) => changeFilter(() => setCategoryId(event.target.value || null))}
            className={FIELD}
          >
            <option value="">כל המחלקות</option>
            {(categories.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name} ({category.product_count})
              </option>
            ))}
          </select>
        </div>

        <Button
          variant="primary"
          onClick={() => {
            setError(null);
            setIsAdding(true);
          }}
        >
          <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
          הוספת מוצר
        </Button>
      </div>

      <label className="flex min-h-11 items-center gap-2 text-body">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(event) => changeFilter(() => setIncludeInactive(event.target.checked))}
          className="size-5"
        />
        להציג גם מוצרים שאינם פעילים
      </label>

      {found.isPending && <Skeleton className="h-40" />}
      {found.isError && <ErrorState onRetry={() => void found.refetch()} />}

      {found.data && total === 0 && (
        <EmptyState
          title={term.trim() || categoryId ? 'לא נמצא מוצר' : 'אין עדיין מוצרים'}
          description={
            term.trim() || categoryId
              ? 'אפשר לנקות את החיפוש או לבחור מחלקה אחרת.'
              : 'אפשר להוסיף מוצר ראשון.'
          }
        />
      )}

      {found.data && total > 0 && (
        <>
          <p className="m-0 text-body-sm text-muted">
            {total === 1 ? 'מוצר אחד' : `${total} מוצרים`}
            {shownSoFar < total && `, מוצגים ${shownSoFar}`}
          </p>

          <Table>
            <thead>
              <Tr>
                <Th>שם</Th>
                <Th>מחלקות</Th>
                <Th>מק״ט</Th>
                <Th>מצב</Th>
                <Th>
                  <span className="sr-only">פעולות</span>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {rows.map((product) => (
                <Tr key={product.id}>
                  <Td>{product.name}</Td>
                  <Td>
                    {product.categories.length === 0 ? (
                      <span className="text-muted">בלי מחלקה</span>
                    ) : (
                      <span className="flex flex-wrap gap-1">
                        {product.categories.map((category) => (
                          <span
                            key={category.id}
                            className="rounded-pill border border-muted px-2 py-0.5 text-label"
                          >
                            {category.name}
                          </span>
                        ))}
                      </span>
                    )}
                  </Td>
                  <Td className="tabular">{product.sku ?? '—'}</Td>
                  <Td>
                    {product.is_active ? (
                      <StatusBadge tone="good">פעיל</StatusBadge>
                    ) : (
                      <span className="text-muted">לא פעיל</span>
                    )}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        aria-label={`עריכת ${product.name}`}
                        onClick={() => {
                          setError(null);
                          setEditingId(product.id);
                        }}
                      >
                        <Pencil size={20} strokeWidth={1.75} aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label={`מחיקת ${product.name}`}
                        onClick={() =>
                          undo.remove({
                            id: product.id,
                            message: `"${product.name}" נמחק`,
                            perform: () => remove.mutateAsync(product.id),
                          })
                        }
                      >
                        <Trash2 size={20} strokeWidth={1.75} aria-hidden="true" />
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          {/* עימוד ולא גלילה אינסופית: אחרי עריכת מוצר חוזרים לאותו
              עמוד, ולגלילה אינסופית אין "אותו מקום" לחזור אליו */}
          {total > PRODUCT_PAGE_SIZE && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                הקודם
              </Button>
              <span className="text-body-sm text-muted">
                עמוד {page + 1} מתוך {Math.ceil(total / PRODUCT_PAGE_SIZE)}
              </span>
              <Button
                variant="secondary"
                disabled={shownSoFar >= total}
                onClick={() => setPage((current) => current + 1)}
              >
                הבא
              </Button>
            </div>
          )}
        </>
      )}

      {isAdding && (
        <ProductDrawer
          draft={emptyProduct()}
          categories={categories.data ?? []}
          isSaving={save.isPending}
          onClose={() => setIsAdding(false)}
          onSave={async (draft) => {
            setError(null);
            try {
              await save.mutateAsync(draft);
              setIsAdding(false);
            } catch (caught) {
              setError(errorMessage(caught));
              throw caught;
            }
          }}
        />
      )}

      {editingId && (
        <EditProductDrawer
          productId={editingId}
          categories={categories.data ?? []}
          isSaving={save.isPending}
          onClose={() => setEditingId(null)}
          onSave={async (draft) => {
            setError(null);
            try {
              await save.mutateAsync(draft);
              setEditingId(null);
            } catch (caught) {
              setError(errorMessage(caught));
              throw caught;
            }
          }}
        />
      )}
    </AdminPage>
  );
}

type CategoryOption = { id: string; name: string; product_count: number };

/** טוען את המוצר המלא ואז פותח את אותו טופס. */
function EditProductDrawer({
  productId,
  categories,
  isSaving,
  onClose,
  onSave,
}: {
  productId: string;
  categories: CategoryOption[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (draft: ProductDraft) => Promise<void>;
}) {
  const product = useProductForEdit(productId);

  if (product.isPending || !product.data) {
    return (
      <Drawer title="טוען מוצר" onClose={onClose}>
        <Skeleton className="h-64" />
      </Drawer>
    );
  }

  return (
    <ProductDrawer
      draft={product.data}
      categories={categories}
      isSaving={isSaving}
      onClose={onClose}
      onSave={onSave}
    />
  );
}

/**
 * טופס המוצר, במגש ולא בתוך הדף.
 *
 * מגש ולא דיאלוג, לפי מערכת העיצוב: דיאלוג עוצר כדי לשאול שאלה
 * אחת, וכאן יש שמונה שדות ובחירת מחלקות — זה מקום עבודה.
 */
function ProductDrawer({
  draft: initial,
  categories,
  isSaving,
  onClose,
  onSave,
}: {
  draft: ProductDraft;
  categories: CategoryOption[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (draft: ProductDraft) => Promise<void>;
}) {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState('');
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const { add: addCategory } = useCategoryMutations();

  const set = <K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const toggleCategory = (id: string) =>
    setDraft((current) => ({
      ...current,
      category_ids: current.category_ids.includes(id)
        ? current.category_ids.filter((x) => x !== id)
        : [...current.category_ids, id],
    }));

  const pickImage = (file: File) => {
    setFileError(null);
    setBusy(true);
    uploadImage('products', file)
      .then((path) => set('image_path', path))
      .catch((caught) => setFileError(errorMessage(caught)))
      .finally(() => setBusy(false));
  };

  return (
    <Drawer
      title={draft.id === null ? 'מוצר חדש' : draft.name || 'עריכת מוצר'}
      subtitle={draft.id === null ? 'שם, פרטים, ולאילו מחלקות הוא שייך' : undefined}
      onClose={onClose}
      footer={(close) => (
        <>
          <Button
            variant="primary"
            disabled={isSaving || busy || draft.name.trim() === ''}
            onClick={() => {
              void onSave(draft).then(close).catch(() => undefined);
            }}
          >
            {draft.id === null ? 'הוספת המוצר' : 'שמירת המוצר'}
          </Button>
          <Button variant="secondary" onClick={close}>
            ביטול
          </Button>
        </>
      )}
    >
      <div className="grid gap-3">
        <Field label="שם המוצר *" htmlFor="p-name">
          <input
            id="p-name"
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
            className={FIELD}
          />
        </Field>

        <Field label="שם לועזי" htmlFor="p-name-en">
          <input
            id="p-name-en"
            dir="ltr"
            value={draft.name_en}
            onChange={(event) => set('name_en', event.target.value)}
            className={FIELD}
          />
        </Field>

        <Field label="מק״ט" htmlFor="p-sku">
          <input
            id="p-sku"
            dir="ltr"
            value={draft.sku}
            onChange={(event) => set('sku', event.target.value)}
            className={FIELD}
          />
        </Field>

        <fieldset className="grid gap-2 rounded-md border border-line p-3">
          <legend className="px-1 text-label font-medium">מחלקות</legend>
          <p className="m-0 text-body-sm text-muted">
            אפשר כמה. מוצר ששייך לשתי מחלקות יופיע בשתיהן.
          </p>
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const active = draft.category_ids.includes(category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleCategory(category.id)}
                    className={cn(
                      'min-h-11 cursor-pointer rounded-pill border px-4 text-label font-medium',
                      active
                        ? 'border-olive bg-olive text-on-olive'
                        : 'border-muted bg-transparent text-ink',
                    )}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          )}

          {/*
            פתיחת מחלקה כאן ולא רק במסך המחלקות: מגלים שחסרה
            מחלקה בדיוק כשאין לאן לשייך את המוצר, ומעבר למסך אחר
            באמצע טופס פתוח מאבד את מה שכבר הוקלד.
          */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="grid grow gap-1">
              <label htmlFor="new-category-inline" className="text-body-sm text-muted">
                מחלקה חדשה
              </label>
              <input
                id="new-category-inline"
                value={newCategory}
                onChange={(event) => setNewCategory(event.target.value)}
                placeholder="שם המחלקה"
                className={FIELD}
              />
            </div>
            <Button
              variant="secondary"
              disabled={newCategory.trim() === '' || addCategory.isPending}
              onClick={() => {
                setCategoryError(null);
                addCategory
                  .mutateAsync(newCategory)
                  .then((id) => {
                    setNewCategory('');
                    setDraft((current) =>
                      current.category_ids.includes(id)
                        ? current
                        : { ...current, category_ids: [...current.category_ids, id] },
                    );
                  })
                  .catch((caught: unknown) => setCategoryError(errorMessage(caught)));
              }}
            >
              <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
              פתיחה ושיוך
            </Button>
          </div>
          {categoryError && (
            <p role="alert" className="m-0 text-body-sm text-bad">
              {categoryError}
            </p>
          )}
        </fieldset>

        <Field label="על המוצר" htmlFor="p-description">
          <textarea
            id="p-description"
            rows={3}
            value={draft.description}
            onChange={(event) => set('description', event.target.value)}
            className={`${FIELD} py-2`}
          />
        </Field>

        <Field label="מה להגיד ללקוח" htmlFor="p-points">
          <textarea
            id="p-points"
            rows={4}
            value={draft.selling_points}
            onChange={(event) => set('selling_points', event.target.value)}
            className={`${FIELD} py-2`}
          />
        </Field>

        <Field label="תמונת המוצר" htmlFor="p-image">
          <div className="grid gap-1">
            <input
              id="p-image"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/heic"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) pickImage(file);
              }}
              className="text-body-sm"
            />
            {busy && <p className="m-0 text-body-sm">מעלה…</p>}
            {!busy && draft.image_path && <p className="m-0 text-body-sm text-good">תמונה נטענה.</p>}
            {fileError && (
              <p role="alert" className="m-0 text-body-sm text-bad">
                {fileError}
              </p>
            )}
          </div>
        </Field>

        <Field label="נכנס למכירה" htmlFor="p-launched">
          <input
            id="p-launched"
            type="date"
            value={draft.launched_at}
            onChange={(event) => set('launched_at', event.target.value)}
            className={FIELD}
          />
        </Field>

        <label className="flex min-h-11 items-center gap-2 text-body">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={(event) => set('is_active', event.target.checked)}
            className="size-5"
          />
          פעיל בקטלוג
        </label>
      </div>
    </Drawer>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1">
      <label htmlFor={htmlFor} className="text-body-sm text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}
